<template>
    <Teleport to="body">
        <div v-show="displayModal"
            ref="theModal"
            class="modal fade show"
            :id="idKey + 'Modal'"
            tabindex="-1"
            :aria-hidden="isAriaHidden"
            v-on:keyup.esc="closeModal()"
            style="display: block;">
            <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable"
                :class="getSizeClass">
                <div class="modal-content rounded-4 p-4">
                    <div class="modal-header bg-white pb-0">
                        <span v-show="showIcon()" class="float-start icon">
                            <template v-if="showCustomIconOverride">
                                <img :src="customIconOverride" style="width: 1.4rem;" />
                            </template>
                            <template v-else>
                                <i class="bi" :class="icon"></i>
                            </template>
                        </span>
                        <h3 class="modal-title">{{ title }}</h3>
                        <button v-if="showClose"
                                ref="closeButton"
                                type="button"
                                class="btn-close d-flex justify-content-center align-items-center"
                                v-on:click="closeModal()"
                                :aria-label="closeButtonAriaLabel">
                            <i class="bi bi-x-lg btn-close-x"></i>
                        </button>
                    </div>
                    <div class="modal-body py-0">
                        <slot></slot>
                    </div>
                </div>
            </div>
        </div>
        <div v-show="displayModal"
            class="modal-backdrop fade show"
            id="backdrop"
            style="display: block;"></div>
    </Teleport>
</template>

<script setup lang="ts">
    import { ref, computed, nextTick } from "vue";

    const props = defineProps({
        idKey: { type: String, required: true },
        title: { type: String, required: true },
        icon: { type: String, required: false, default: "" },
        isWide: { type: Boolean, required: false, default: false },
        showClose: { type: Boolean, default: true },
        returnFocusTo: { type: HTMLElement, required: false },
        checkForCustomIcon: { type: Boolean, default: false, required: false },
        customIconOverride: { type: String, default: "", required: false },
        closeButtonAriaLabel: { type: String, default: "Close", required: false }
    });

    defineExpose({ getModalId, showModal, closeModal })

    const theModal = ref(null);
    const closeButton = ref(null);
    const displayModal = ref(false);
    const isAriaHidden = ref(true);

    const getSizeClass = computed<string>(() => {
        return props.isWide ? "modal-lg" : "";
    });

    const showCustomIconOverride = computed<Boolean>(() => props.customIconOverride.length > 0);

    function getModalId() {
        return props.idKey + "Modal";
    }

    function showIcon() {
        return props.icon.length > 0;
    }

    async function showModal(initializer: () => void) {
        isAriaHidden.value = false;
        await nextTick();

        // Keep focus within modal while showing
        document.getElementById("app")?.setAttribute("inert", "");
        document.body.style.overflow = "hidden";

        displayModal.value = true;

        if (initializer) {
            nextTick(initializer);
        } else {
            requestAnimationFrame(() => {
                closeButton.value?.focus();
            });
        }
    }

    function closeModal() {
        isAriaHidden.value = true;

        if (props.returnFocusTo) {
            requestAnimationFrame(() => {
                props.returnFocusTo?.focus();
            });
        }

        // Remove modal focus
        document.getElementById("app")?.removeAttribute("inert");
        document.body.style.overflow = "";

        displayModal.value = false;
    }
</script>

<style scoped>
    .modal-dialog-scrollable .modal-content {
        overflow: visible;
    }

    .btn-close {
        background-color: #fff;
        border-radius: 50px;
        padding: 19px !important;
        position: absolute;
        top: -20px;
        left: 50%;
        margin-left: -30px !important;
        opacity: 1;
        transition: transform .2s;
    }

        .btn-close:hover {
            transform: scale(1.1);
            opacity: 1;
        }

    .btn-close-x {
        font-size: 1.6rem;
    }

    .icon {
        margin-right: 10px;
        display: none;
    }

    .modal-header {
        border-bottom: none !important;
    }
</style>
